import * as React from 'react'
import { useSelector, useDispatch } from 'react-redux'
import {
  Box,
  Typography as MUITypography,
  styled,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material'
import { DataGrid, gridClasses, esES } from '@mui/x-data-grid'
import { fetchAllTenants, deleteTenant } from '../../../services/tenantService'
import { setIsStored } from '../../../redux/slices/Alerts'
import { grey } from '@mui/material/colors'
import { getColumns } from './columns'
import Botton from '../../BottonCustom'
import { Create } from '@mui/icons-material'
import TenantFormModal from './FormModal'
import AlertCustom from '../../Alerts'

const styleContainer = {
  height: '100%',
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
}
const styleDefault = theme => {
  const primary = `var(--color-primary, ${theme.palette.primary.main})`

  return {
    color: primary,
    border: `1px solid ${primary}`,
    width: '100%',
    fontSize: '12px',
    '&:hover': {
      backgroundColor: primary,
      borderColor: primary,
      color: theme.palette.common.white,
    },
    '&:active': { boxShadow: 'none', opacity: 0.9 },
  }
}

const styleHeaderTab = {
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignContent: 'center',
  alignItems: 'baseline',
  mx: 4,
  mb: 1,
}

const Typography = styled(MUITypography)(props => ({
  fontFamily: props.theme.typography.fontFamily,
  fontStyle: 'normal',
  fontSize: '1em',
  fontWeight: 650,
  lineHeight: '18px',
  color: '#6A6A6A',
  paddingBottom: 6,
  paddingTop: 6,
  textAlign: 'center',
  marginTop: 12,
  marginBottom: 3,
  height: '5%',
}))

const TenantTable = () => {
  const modalRef = React.useRef()
  const alertRef = React.useRef()
  const dispatch = useDispatch()

  const [data, setData] = React.useState([])
  const [pageSize, setPageSize] = React.useState(25)
  const [loading, setLoading] = React.useState(true)
  const [selectedRow, setSelectedRow] = React.useState(null)

  // Dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false)
  const [rowToDelete, setRowToDelete] = React.useState(null)

  const { isStored } = useSelector(state => state.alertsGlobal)

  const loadData = React.useCallback(async () => {
    setLoading(true)
    try {
      const tenants = await fetchAllTenants()
      setData(tenants)
    } catch (err) {
      console.error('Error loading tenants:', err)
    } finally {
      setLoading(false)
    }
  }, [])

  const handleEdit = row => {
    setSelectedRow(row)
    if (modalRef.current) {
      modalRef.current.openModal()
    }
  }

  const handleDeleteClick = React.useCallback(row => {
    setRowToDelete(row)
    setDeleteDialogOpen(true)
  }, [])

  const confirmDelete = async () => {
    if (!rowToDelete) return
    setLoading(true)
    setDeleteDialogOpen(false)
    try {
      const response = await deleteTenant(rowToDelete.id)
      if (response.success) {
        dispatch(
          setIsStored({
            stored: true,
            message: response.message || 'Tenant eliminado correctamente',
          }),
        )
        loadData()
      } else {
        dispatch(setIsStored({ stored: false, message: response.message }))
      }
    } catch (error) {
      dispatch(setIsStored({ stored: false, message: 'Error al eliminar el tenant' }))
    } finally {
      setLoading(false)
      setRowToDelete(null)
    }
  }

  const handleCancelDelete = () => {
    setDeleteDialogOpen(false)
    setRowToDelete(null)
  }

  const columns = React.useMemo(
    () => getColumns(handleEdit, handleDeleteClick, loadData),
    [handleDeleteClick, loadData],
  )

  const handleFormCreation = () => {
    setSelectedRow(null) // Reset for create mode
    if (modalRef.current) {
      modalRef.current.openModal()
    }
  }

  const handleModalClose = () => {
    setSelectedRow(null)
  }

  React.useEffect(() => {
    loadData()
  }, [loadData])

  React.useEffect(() => {
    if (!Boolean(isStored)) return
    alertRef?.current?.openAlert(true)
  }, [isStored])

  return (
    <Box sx={styleContainer}>
      <Box sx={styleHeaderTab}>
        <Box>
          <Typography>Administrar Tenants</Typography>
        </Box>
        <Box>
          <Botton
            text="Nuevo Tenant"
            onClick={handleFormCreation}
            startIcon={<Create />}
            styleButton={styleDefault}
            size="small"
          />
        </Box>
      </Box>
      <div style={{ flexGrow: 1, width: '100%', minHeight: 0 }}>
        <DataGrid
          rows={data}
          columns={columns}
          pageSize={pageSize}
          loading={loading}
          getRowId={row => row.id}
          rowsPerPageOptions={[25, 50, 75]}
          onPageSizeChange={newPageSize => setPageSize(newPageSize)}
          localeText={esES.components.MuiDataGrid.defaultProps.localeText}
          getRowSpacing={params => ({
            top: params.isFirstVisible ? 0 : 5,
            bottom: params.isLastVisible ? 0 : 5,
          })}
          sx={{
            ml: 2,
            [`& .${gridClasses.row}`]: {
              bgcolor: grey[100],
            },
          }}
          disableSelectionOnClick
        />
      </div>
      <TenantFormModal
        ref={modalRef}
        initialValues={selectedRow}
        onClose={handleModalClose}
        onSuccess={() => {
          loadData()
          dispatch(setIsStored({ stored: true, message: 'Guardado exitosamente' }))
        }}
      />
      <AlertCustom
        ref={alertRef}
        severity={isStored?.stored !== true ? 'warning' : 'success'}>
        {isStored?.message ||
          (isStored?.stored !== true ? 'Error desconocido' : 'Guardado exitosamente.')}
      </AlertCustom>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleCancelDelete}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description">
        <DialogTitle id="alert-dialog-title">{'¿Estás seguro?'}</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            {rowToDelete
              ? `Se eliminará el tenant "${rowToDelete.nombre || rowToDelete.name}". Esta acción no se puede deshacer.`
              : 'Confirmar eliminación'}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete}>Cancelar</Button>
          <Button onClick={confirmDelete} color="error" autoFocus>
            Sí, eliminar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default TenantTable
